import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  AppInsightService,
  BrowserEventService,
  Category,
  LobbyType,
  LogEvents,
  StorageService,
  StorageType
} from '@rct/lynx-lobbies-shared-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LYNX_STORAGE_KEYS } from '../../../../core/constants/lynx-storage-keys.constant';
import { LynxLobbyService } from '../../../../core/services/lobby.service';
import { LynxLocalizeRouterService } from '../../../../core/services/localize-router.service';

@Component({
    selector: 'lynx-category-container',
    templateUrl: './category-container.component.html',
    styleUrls: ['./category-container.component.scss'],
    standalone: false
})
export class CategoryContainerComponent implements OnInit, OnDestroy {
  private _appInsightService: AppInsightService = inject(AppInsightService);
  private _localizeRouterService: LynxLocalizeRouterService = inject(
    LynxLocalizeRouterService
  );
  private _storageService: StorageService = inject(StorageService);
  private _lobbyService: LynxLobbyService = inject(LynxLobbyService);
  private _router: Router = inject(Router);
  private _browserEventService: BrowserEventService =
    inject(BrowserEventService);
  private _destroy$ = new Subject<boolean>();

  @Input() categories: Category[] = [];
  @Output() back: EventEmitter<void> = new EventEmitter<void>();

  lobbyId: string;
  lobbyTemplateId: string;
  selectedCategoryId: string | null = null;

  isFirstRender: boolean = true;
  isPopup: boolean = false;
  isCategoriesExtendedMap: { [categoryId: string]: boolean } = {};
  isSelectedCategoryFirstClick: boolean = false;

  lottieStaticAssetFolderPath: string = './assets/lottie/static';
  lottieAnimatedAssetFolderPath: string = './assets/lottie/animated';

  get isMobile(): boolean {
    return this._browserEventService.isMobile();
  }

  ngOnInit(): void {
    this.lobbyId = this._storageService.get(
      LYNX_STORAGE_KEYS.lobbyId,
      StorageType.Session
    );

    this._lobbyService.lobbyTemplateId$
      .pipe(takeUntil(this._destroy$))
      .subscribe((lobbyTemplateId) => {
        this.lobbyTemplateId = lobbyTemplateId;
      });

    this.setActiveCategory();

    this._router.events.pipe(takeUntil(this._destroy$)).subscribe((events) => {
      if (events instanceof NavigationEnd) {
        this.setActiveCategory();
      }
    });
  }

  onMouseEnterCategory(category: Category, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const lottiePlayerElement = (event.target as HTMLElement).querySelector(
      'lottie-player'
    ) as any;

    if (lottiePlayerElement && this.isAnimatedIconType(category.icon)) {
      lottiePlayerElement.play();
      lottiePlayerElement.setLooping(true);
    }
  }

  onMouseLeaveCategory(category: Category, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const lottiePlayerElement = (event.target as HTMLElement).querySelector(
      'lottie-player'
    ) as any;

    if (lottiePlayerElement && this.isAnimatedIconType(category.icon)) {
      if (this.isSelectedCategoryFirstClick) {
        lottiePlayerElement.setLooping(false);
        this.isSelectedCategoryFirstClick = false;
      } else {
        lottiePlayerElement.setLooping(false);
        lottiePlayerElement.stop();
      }
    }
  }

  onCategoryClick(category: Category, event: Event) {
    event.preventDefault();
    event.stopPropagation();

    const lottiePlayerElement = (event.target as HTMLElement).querySelector(
      'lottie-player'
    ) as any;

    if (
      lottiePlayerElement &&
      lottiePlayerElement.getLottie &&
      lottiePlayerElement.getLottie() &&
      this.isAnimatedIconType(lottiePlayerElement.getLottie().fileName)
    ) {
      lottiePlayerElement.play();
      lottiePlayerElement.setLooping(false);
    }

    this._appInsightService.logEvent(
      { storageKeys: LYNX_STORAGE_KEYS, lobbyType: LobbyType.Lynx },
      LogEvents.onCategoryClick,
      {
        category: category.categoryName,
        lobbyId: this.lobbyId,
        lobbyTemplateId: this.lobbyTemplateId
      }
    );

    this._appInsightService.gaEvent(
      { storageKeys: LYNX_STORAGE_KEYS, lobbyType: LobbyType.Lynx },
      LogEvents.onCategoryClick,
      `category: ${category.categoryName}, lobbyId: ${this.lobbyId.split('-').slice(0, 2).join('-')}, lobbyTemplateId: ${this.lobbyTemplateId.split('-').slice(0, 2).join('-')}`
    );

    this.selectedCategoryId = category.categoryId;
    this.isSelectedCategoryFirstClick = true;

    this._localizeRouterService.translateRoute(
      `home/category/${category.categoryId}`
    );
  }

  onExpandCategory(category: Category, event: Event) {
    event.preventDefault();
    event.stopPropagation();

    this.isCategoriesExtendedMap[category.categoryId] =
      !this.isCategoriesExtendedMap[category.categoryId];

    if (!this.isChildSelected(category)) {
      this.onCategoryClick(category.subCategories[0], event);
    }
  }

  isAnimatedIconType(iconName: string): boolean {
    if (iconName) {
      return iconName.includes('-colored') ? true : false;
    } else {
      return false;
    }
  }

  getIconAssetPath(category: Category): string {
    const iconAssetPath = this.isAnimatedIconType(category.icon)
      ? this.getAnimatedIconAssetPath(category)
      : this.getStaticIconAssetPath(category);

    return iconAssetPath;
  }

  getStaticIconAssetPath(category: Category): string {
    return `${this.lottieStaticAssetFolderPath}/${category.icon}.json`;
  }

  getAnimatedIconAssetPath(category: Category): string {
    if (!this.isAnimatedIconType(category.icon)) return '';

    return `${this.lottieAnimatedAssetFolderPath}/${category.icon}-animated.json`;
  }

  setActiveCategory(): void {
    const id = this._router.url.split('/').pop();

    this.selectedCategoryId = id;

    this.categories.forEach((category: Category) => {
      if (this.isChildSelected(category)) {
        this.isCategoriesExtendedMap[category.categoryId] = true;
      }
    });
  }

  hasSubCategories(category: Category): boolean {
    return (
      category &&
      category.subCategories &&
      category.subCategories.length &&
      category.subCategories.length > 0
    );
  }

  isChildSelected(category: Category): boolean {
    if (this.selectedCategoryId === null) {
      return false;
    }

    if (this.selectedCategoryId === category.categoryId) {
      return true;
    }

    if (!this.hasSubCategories(category)) {
      return false;
    }

    return category.subCategories.some((subCategory: Category) =>
      this.isChildSelected(subCategory)
    );
  }

  isRootCategory(category: Category): boolean {
    return !!this.categories.find(
      (c: Category) => c.categoryId === category.categoryId
    );
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }
}
